/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Row,
  Col,
  Spin,
  Card,
  Tabs,
  Button,
  DatePicker,
} from 'antd';
import Table from 'components/Table';
import { onError } from 'utils/handlers';
import FormSubmitControls from 'components/FormSubmitControls';
import Direccion, { permissionConfig as permissionConfigDireccion } from 'components/Direccion';
import { DATE_FRONTEND_FORMAT, discartNullValues } from 'utils/formatValues';
import { makeGetOnFinish, makeOnChangeTabKey } from 'utils/tabsManagement';
import makeMessager from 'utils/complexFormsMessages';
import {
  getPeritosDePredios,
  patchPeritoDePredio,
  postPeritoDePredio,
} from 'api/catastro/peritosDePredios';
import { isMutable } from 'utils/estadosGlobales';
import {
  customUniqueValidator,
  generateCURPRules,
  generateEmailRules,
  generateRFCRules,
} from 'utils/rules';
import Select from 'components/Select';
import { getTiposDePersonas } from 'api/catalogos/tipoDePersona';
import { baseURI, getContribuyentes } from 'api/empresas/contribuyentes';

const { TabPane } = Tabs;

export const permissionConfig = {
  permissionModel: 'perito_catastro',
  catalogsModels: ['contribuyente', 'tipodepersona'],
  tabsModels: [permissionConfigDireccion],
};

const PeritosPredios = ({ permission, addressPermission }) => {
  const [form] = Form.useForm();
  const tipo_de_persona = Form.useWatch('tipo_de_persona', form);
  const [data, setData] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(false);
  const [mutable, setMutable] = useState(false);
  const [formAddress, setFormAddress] = useState(null);
  const [selectedPerito, setSelectedPerito] = useState();
  const [currentTabKey, setCurrentTabKey] = useState('general');
  const [tiposDePersonas, setTiposDePersonas] = useState([]);

  const complexForms = [{ key: 'direcciones', formInstance: formAddress, permission: addressPermission }];

  const basicKeys = ['general'];

  const fetchData = async (page = 1, params = {}) => {
    setLoading(true);
    const _data = await getPeritosDePredios({ ...params, page });
    _data.params = params;
    _data.params.page = page;
    setData(_data);
    setLoading(false);
  };

  const showMsg = makeMessager(setCurrentTabKey, fetchData);

  const onChangeTabKey = makeOnChangeTabKey(
    basicKeys,
    complexForms,
    currentTabKey,
    setCurrentTabKey,
    showMsg,
  );

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      const _tiposDePersonas = await getTiposDePersonas();
      await fetchData();
      if (mounted) setTiposDePersonas(_tiposDePersonas);
    };
    fetch();
    return () => {
      mounted = false;
    };
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
    setCurrentTabKey('general');
    setMutable(true);
    setSelectedPerito();
  };

  const handleOnRowClick = async (_record) => {
    const record = discartNullValues(_record);
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(_record);
    setSelectedPerito(_record);
    setMutable(isMutable(record));
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = async () => setVisible(true);

  const onFinish = async (_continue = false) => {
    if (!mutable) return null;
    setLoading(true);
    try {
      await form.validateFields();
      const values = form.getFieldsValue();
      values.nombre_completo = ['nombre', 'apellido_paterno', 'apellido_materno']
        .map((f) => values[f] || '').filter((e) => e).join(' ').trim();
      const [key] = selectedRowKeys;
      const saved = await (key ? patchPeritoDePredio(key, values, false)
        : postPeritoDePredio(values));
      if (saved) {
        if (_continue) {
          await onClickEdit(saved.id);
          if (!key) setTimeout(() => setSelectedRowKeys([saved.id]));
        } else {
          onCancel();
        }
        await fetchData();
        return saved;
      }
      return null;
    } catch (error) {
      onError(error, setLoading);
    } finally {
      setLoading(false);
    }
    return null;
  };

  const getOnFinish = makeGetOnFinish(basicKeys, complexForms, currentTabKey, onFinish);

  const rules = {
    required: [{ required: true }],
    clave: [
      customUniqueValidator({
        required: true,
        errorMessage: 'Ya existe un Perito con la clave ingresada',
        getter: getContribuyentes,
      }),
    ],
    curp: generateCURPRules({
      required: false,
      label: 'Perito',
      getter: getContribuyentes,
      validateUnique: true,
    }),
    rfc: generateRFCRules({
      required: false,
      label: 'Perito',
      getter: getContribuyentes,
      validateUnique: true,
    }),
    correo_electronico: generateEmailRules({
      required: false,
      label: 'Perito',
      getter: getContribuyentes,
      validateUnique: true,
    }),
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 100,
    },
    {
      titleText: 'Nombre Completo',
      dataIndex: 'nombre_completo',
      key: 'nombre_completo',
      width: 400,
    },
    {
      titleText: 'Vigencia',
      dataIndex: 'fecha_vigencia',
      key: 'fecha_vigencia',
      width: 100,
      render: (val) => val?.format(DATE_FRONTEND_FORMAT),
    },
  ];

  return (
    <Row align="center" justify="center" className="container">
      <Col xs={24} sm={24} md={24}>
        <Spin tip="Cargando..." spinning={loading} style={{ width: '100%', height: '100%' }}>
          {!visible ? (
            <Table
              cols={columns}
              data={data.results}
              rowSelection={rowSelection}
              handleOnRowClick={handleOnRowClick}
              controls={{
                onClickEdit,
                onClickAdd,
              }}
              rowKey="id"
              count={data?.count}
              hasAllViewPermission
              permission={permission}
              allowSort={false}
              pagination={false}
              paginationCallback={(p, params) => fetchData(p, params)}
              currentPage={data.params?.page}
            />
          ) : (
            <Col span={24} style={{ height: '100%' }}>
              <Card
                className="form-container no-padding"
                title={selectedPerito ? selectedPerito.clave : 'Perito'}
                extra={(
                  <FormSubmitControls
                    onCancel={onCancel}
                    mutable={mutable}
                    allowSaveAndContinue
                    onFinish={getOnFinish()}
                    loading={loading}
                    setLoading={setLoading}
                  />
                )}
                bordered={false}
              >
                <Tabs
                  onChange={onChangeTabKey}
                  activeKey={currentTabKey}
                  type="card"
                >
                  <TabPane tab="General" key="general">
                    <Form
                      layout="vertical"
                      form={form}
                      scrollToFirstError
                      initialValues={{
                        tipo_de_persona: 1,
                      }}
                      name="general"
                      onFinish={() => onFinish()}
                      disabled={!mutable}
                    >
                      <Row gutter={10}>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            name="fecha_vigencia"
                            label="Fecha Vigencia"
                            rules={rules.required}
                          >
                            <DatePicker
                              placeholder=""
                              format={DATE_FRONTEND_FORMAT}
                            />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            label="Clave"
                            name="clave"
                            rules={rules.clave}
                          >
                            <Input disabled allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            label="Nombre"
                            name="nombre"
                            rules={rules.required}
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            label="Apellido Paterno"
                            name="apellido_paterno"
                            rules={rules.required}
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            label="Apellido Materno"
                            name="apellido_materno"
                          // rules={rules.required}
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            label="Número Perito"
                            name="numero_notaria"
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            label="Cedula"
                            name="cedula_profesional"
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            label="Correo Electronico"
                            name="correo_electronico"
                            rules={rules.correo_electronico}
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            label="Tipo de Persona"
                            name="tipo_de_persona"
                          >
                            <Select dataSource={tiposDePersonas} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={24} md={8}>
                          <Form.Item
                            label="RFC"
                            name="rfc"
                            rules={rules.rfc}
                          >
                            <Input allowClear />
                          </Form.Item>
                        </Col>
                        {tipo_de_persona === 1 && (
                          <Col xs={24} sm={24} md={8}>
                            <Form.Item
                              label="CURP"
                              name="curp"
                              rules={rules.curp}
                            >
                              <Input allowClear />
                            </Form.Item>
                          </Col>
                        )}
                        {/* <Col xs={24} sm={24} md={24}>
                          <Form.Item
                            name="observaciones"
                            label="Observaciones"
                          >
                            <Input.TextArea allowClear autoSize={{ minRows: 3 }} />
                          </Form.Item>
                        </Col> */}
                        <Form.Item hidden><Button htmlType="submit" /></Form.Item>
                      </Row>
                    </Form>
                  </TabPane>
                  {addressPermission.hasAllViewPermission && (
                    <>
                      <TabPane
                        tab="Dirección"
                        key="direcciones"
                        forceRender={!!selectedRowKeys?.length}
                        disabled={!selectedRowKeys.length}
                      >
                        <Direccion
                          setFormAddress={setFormAddress}
                          currentURI={baseURI}
                          parentID={selectedRowKeys[0]}
                          setLoading={setLoading}
                          setCurrentTabKey={setCurrentTabKey}
                          addressPropName="direcciones"
                          callback={(values, _continue) => {
                            if (!_continue) {
                              onCancel();
                            } else {
                              const [key] = selectedRowKeys;
                              const clone = [...data.results];
                              const results = clone.map((e) => (e.id === key
                                ? ({ ...e, direcciones: values?.id }) : e));
                              setData({ ...data, results });
                            }
                          }}
                          permission={addressPermission}
                          mutable={mutable}
                        />
                      </TabPane>
                    </>
                  )}
                </Tabs>
              </Card>
            </Col>
          )}
        </Spin>
      </Col>
    </Row>
  );
};

export default PeritosPredios;
